// AM Advertising Home — the performance pulse: the three ALWAYS-VISIBLE fixed
// windows (7 · 14 · 30 days), each vs its immediately-prior equal window,
// independent of the page's date selection. PURE.
//
// ⚠️ EACH CHANNEL'S WINDOWS END AT ITS OWN asOf — the freshness rule again. A
// lagging Google transfer must not contribute phantom zero-days to "the last 7
// days"; each channel reports its freshest N LOADED days, and the card says
// what "as of" means in the header. Blending (spend converted + summed, leads
// added) happens at the display edge via the rate table, never here.

import type { AdChannel, HomeAsOf, SpineDay } from '../../types/advertisingHome.js';
import type { CurrencyAmount } from '../../types/reporting.js';
import { addDaysYmd, sumWindow, type WindowTotals } from './spine.js';

export const PULSE_WINDOWS = [7, 14, 30] as const;
export type PulseDays = (typeof PULSE_WINDOWS)[number];

export interface PulseChannel {
  current: WindowTotals;
  previous: WindowTotals;
}

/**
 * One day of the card's chart, PER CHANNEL and PER CURRENCY — the raw material
 * the metric × channel selector plots. Deliberately not a pre-computed number:
 * the selector can switch between Spend / Leads / CPL and Blended / Meta /
 * Google without a refetch, and CPL must be RE-DERIVED per bucket (spend ÷
 * leads for that day) rather than averaged from daily CPLs.
 */
export interface PulseBucket {
  date: string;
  meta: { spend: CurrencyAmount[]; leads: number };
  google: { spend: CurrencyAmount[]; leads: number };
}

export interface PulseCard {
  days: PulseDays;
  meta: PulseChannel;
  google: PulseChannel;
  /** Daily buckets over the current window (from the later of the two asOfs). */
  buckets: PulseBucket[];
}

function channelRows(rows: readonly SpineDay[], channel: AdChannel): SpineDay[] {
  return rows.filter((r) => r.channel === channel);
}

function windows(
  rows: readonly SpineDay[],
  asOfDay: string | null,
  days: number,
): PulseChannel {
  if (asOfDay === null) {
    const empty = sumWindow([], '0000-01-01', '0000-01-01');
    return { current: empty, previous: empty };
  }
  const curFrom = addDaysYmd(asOfDay, -(days - 1));
  const prevTo = addDaysYmd(curFrom, -1);
  const prevFrom = addDaysYmd(prevTo, -(days - 1));
  return {
    current: sumWindow(rows, curFrom, asOfDay),
    previous: sumWindow(rows, prevFrom, prevTo),
  };
}

export function buildPulse(rows: readonly SpineDay[], asOf: HomeAsOf): PulseCard[] {
  const meta = channelRows(rows, 'meta');
  const google = channelRows(rows, 'google');
  const sparkAnchor = [asOf.meta, asOf.google].filter((d): d is string => d !== null).sort().at(-1) ?? null;

  return PULSE_WINDOWS.map((days) => ({
    days,
    meta: windows(meta, asOf.meta, days),
    google: windows(google, asOf.google, days),
    buckets: sparkAnchor ? buildBuckets(rows, addDaysYmd(sparkAnchor, -(days - 1)), sparkAnchor) : [],
  }));
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Per-day, per-channel, per-currency spend + leads over [from..to]. */
function buildBuckets(
  rows: readonly SpineDay[],
  from: string,
  to: string,
): PulseBucket[] {
  const byDate = new Map<
    string,
    { meta: Map<string, number>; google: Map<string, number>; metaLeads: number; googleLeads: number }
  >();
  for (const r of rows) {
    if (r.date === null || r.date < from || r.date > to) continue;
    let day = byDate.get(r.date);
    if (!day) {
      day = { meta: new Map(), google: new Map(), metaLeads: 0, googleLeads: 0 };
      byDate.set(r.date, day);
    }
    const spendBy = r.channel === 'meta' ? day.meta : day.google;
    spendBy.set(r.currency, (spendBy.get(r.currency) ?? 0) + r.spend);
    if (r.channel === 'meta') day.metaLeads += r.leads;
    else day.googleLeads += r.leads;
  }
  const toAmounts = (m: Map<string, number>): CurrencyAmount[] =>
    [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([currency, amount]) => ({ currency, amount: r2(amount) }));

  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, d]) => ({
      date,
      meta: { spend: toAmounts(d.meta), leads: r2(d.metaLeads) },
      google: { spend: toAmounts(d.google), leads: r2(d.googleLeads) },
    }));
}
